/**
 * One Reporting panel by its canvas id, drawn by the panel's own component.
 * Where it lands, the rail or a tile, is the ReportSlot around it. Each
 * source's data is fetched once per client and month, however many places
 * draw its panel.
 */

import React from "react";
import { SpinnerIcon } from "@/components/icons";
import { PLATFORM_REGISTRY } from "@/config/platforms";
import type { ReportComparison, SocialMediaReport, TrendDataPoint } from "../hooks/useSocialMediaReport";
import type { PostDay } from "../utils/dayMarkers";
import { AdPerformanceSection } from "../components/AdPerformanceSection";
import { FollowerPanel } from "../components/FollowerPanel";
import { FollowerWindowPanel } from "../components/FollowerWindowPanel";
import { GoogleSection } from "../components/GoogleSection";
import { GrowthPanel } from "../components/GrowthPanel";
import { MonthOnlyPanel } from "../components/MonthOnlyPanel";
import { PlatformCard } from "../components/PlatformCard";
import { ReportPanel, ReportPanelEmpty, ReportPanelNote } from "../components/ReportPanel";
import { BookingIntentPanel, ContentInsightsPanel, OverviewPanel, TrendsPanel } from "../components/SocialPanels";
import type { ReportPeriod } from "../utils/period";
import { panelTitle, type PanelId } from "./panels";

/** The month's social report, or why the social panels have none. */
export type SocialReportState =
  | { status: "ready"; report: SocialMediaReport; comparison: ReportComparison | null; historicalData: TrendDataPoint[] }
  | { status: "loading" }
  | { status: "missing"; message: string; hint: string };

export interface ReportPanelByIdProps {
  id: PanelId;
  clientId: string;
  period: ReportPeriod;
  /** Today in Rome, which a window's hooks key on. */
  today: string;
  /** Opens Month, from a panel that reads by month only. */
  onMonth(): void;
  social: SocialReportState;
  postDays: ReadonlyArray<PostDay>;
  onRefreshPlatform?: (platform: "facebook" | "instagram" | "tiktok") => void;
  refreshingPlatform?: string | null;
}

/** A social panel while its report loads or when the month has none: the
 *  panel's title with the reason, never a hidden panel. */
const SocialPlaceholder: React.FC<{ id: PanelId; social: Exclude<SocialReportState, { status: "ready" }> }> = ({ id, social }) => {
  if (social.status === "loading") {
    const loadingRow = (
      <div className="flex items-center gap-2 text-sm text-gray-500 py-2">
        <SpinnerIcon className="w-4 h-4" />
        Loading report…
      </div>
    );
    return (
      <ReportPanel title={panelTitle(id)} collapsedBody={loadingRow}>
        {loadingRow}
      </ReportPanel>
    );
  }
  return (
    <ReportPanel title={panelTitle(id)} collapsedBody={<ReportPanelNote>{social.message}</ReportPanelNote>}>
      <ReportPanelEmpty message={social.message} hint={social.hint} />
    </ReportPanel>
  );
};

export const ReportPanelById: React.FC<ReportPanelByIdProps> = ({
  id,
  clientId,
  period,
  today,
  onMonth,
  social,
  postDays,
  onRefreshPlatform,
  refreshingPlatform,
}) => {
  const source = { clientId, period, today };
  switch (id) {
    case "profile":
      return <GoogleSection only="businessProfile" {...source} />;
    case "website":
      return <GoogleSection only="website" {...source} />;
    case "googleads":
      return <GoogleSection only="googleAds" {...source} />;
    case "metaads":
      return <AdPerformanceSection {...source} postDays={postDays} />;
    default:
      break;
  }
  if (period.kind === "window") {
    // Followers read the nightly snapshots, so they window without the social report. Every other panel left
    // reads the monthly report (`byMonth` in panels.ts) and offers Month instead.
    if (id === "followers") return <FollowerWindowPanel clientId={clientId} period={period.period} today={today} />;
    return <MonthOnlyPanel id={id} onMonth={onMonth} />;
  }
  if (id === "growth") {
    // A social Refresh refetches it: useReportingView invalidates growth.
    return <GrowthPanel clientId={clientId} year={period.year} month={period.month} />;
  }
  if (social.status !== "ready") return <SocialPlaceholder id={id} social={social} />;
  const { report, comparison, historicalData } = social;
  const props = { report, comparison, historicalData, clientId };
  switch (id) {
    case "overview":
      return <OverviewPanel {...props} />;
    case "followers":
      return <FollowerPanel report={report} comparison={comparison} historicalData={historicalData} />;
    case "trends":
      return <TrendsPanel {...props} />;
    case "booking":
      return <BookingIntentPanel {...props} />;
    case "content":
      return <ContentInsightsPanel {...props} />;
    case "facebook":
    case "instagram":
    case "tiktok": {
      const config = PLATFORM_REGISTRY[id];
      if (!config) return null;
      return (
        <PlatformCard
          platformKey={id}
          config={config}
          report={report}
          comparison={comparison}
          historicalData={historicalData}
          onRefresh={onRefreshPlatform}
          isRefreshing={refreshingPlatform === id}
          disabled={refreshingPlatform != null}
        />
      );
    }
  }
};
